import { AsyncProgressStatus, ImportTypeConfig } from "../types";
import { FileNameConflictResponse } from "components/ServerValidationErrors";
import {
    StockTransferFileImportResultsResponse,
    performStockTransferFileImport,
    performStockTransferImportFilePreCheck,
    getStockTransferFileImportResults,
} from "./stock-transfer-import-service";
import { useEffect, useRef, useState } from "react";

export type ImportState<TResponse> = {
    error: Error | null | FileNameConflictResponse;
    data: TResponse | null;
    status: AsyncProgressStatus;
};

export function usePerformStockTransferPreCheck(
    fieldCompanyId: number,
    file: File | undefined,
    importTypeConfig: ImportTypeConfig | undefined,
    forceUpload: boolean,
) {
    const [results, setResults] = useState<StockTransferFileImportResultsResponse | null>(null);
    const [error, setError] = useState<Error | null | FileNameConflictResponse>(null);
    const [status, setStatus] = useState(AsyncProgressStatus.NotSubmitted);
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        if (file && importTypeConfig) {
            setResults(null);
            setError(null);
            setStatus(AsyncProgressStatus.InProgress);
            (async () => {
                try {
                    const data = await performStockTransferImportFilePreCheck(
                        fieldCompanyId,
                        file,
                        importTypeConfig.api,
                        mounted,
                        forceUpload,
                    );
                    const status =
                        data.outcome === "Succeeded" ? AsyncProgressStatus.Success : AsyncProgressStatus.Failed;
                    setResults(data);
                    setStatus(status);
                } catch (e) {
                    if (mounted.current) {
                        if (e.status === 409){
                            const validationFailuresResponse = JSON.parse(e.message) as FileNameConflictResponse;
                            setError(validationFailuresResponse);
                            setStatus(AsyncProgressStatus.Failed);
                            return;
                        }
                        setError(e as Error);
                        setStatus(AsyncProgressStatus.Failed);
                    }
                }
            })();
        }
        return () => {
            mounted.current = false;
        };
    }, [fieldCompanyId, file, importTypeConfig, forceUpload]);

    return {
        data: results,
        error,
        status,
    };
}

export function usePerformStockTransferFileImport(
    fieldCompanyId: number,
    file: File | undefined,
    importTypeConfig: ImportTypeConfig | undefined,
    forceUpload: boolean
) {
    const [results, setResults] = useState<StockTransferFileImportResultsResponse | null>(null);
    const [error, setError] = useState<Error | null | FileNameConflictResponse>(null);
    const [status, setStatus] = useState(AsyncProgressStatus.NotSubmitted);
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        if (file && importTypeConfig) {
            setResults(null);
            setError(null);
            setStatus(AsyncProgressStatus.InProgress);

            (async () => {
                try {
                    const data = await performStockTransferFileImport(
                        fieldCompanyId,
                        file,
                        importTypeConfig.api,
                        mounted,
                        forceUpload
                    );
                    const status =
                        data.outcome === "Succeeded" ? AsyncProgressStatus.Success : AsyncProgressStatus.Failed;
                    setResults(data);
                    setStatus(status);
                } catch (e) {
                    if (mounted.current) {
                        if (e.status === 409){
                            const validationFailuresResponse = JSON.parse(e.message) as FileNameConflictResponse;
                            setError(validationFailuresResponse);
                            setStatus(AsyncProgressStatus.Failed);
                            return;
                        }
                        setError(e as Error);
                        setStatus(AsyncProgressStatus.Failed);
                    }
                }
            })();
        }
        return () => {
            mounted.current = false;
        };
    }, [fieldCompanyId, file, importTypeConfig, forceUpload]);

    return {
        data: results,
        error,
        status,
    };
}

export function useGetStockTransferFileImportResults(id: guid) {
    const [results, setResults] = useState<StockTransferFileImportResultsResponse | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setResults(null);
        setError(null);
        setLoading(true);
        (async () => {
            try {
                const data = await getStockTransferFileImportResults(id);
                setResults(data);
                setError(null);
            } catch (error: any) {
                setResults(null);
                setError(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    return {
        data: results,
        error,
        loading
    };
}